import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/Badges";
import { Brain, ArrowLeft, ShieldCheck, QrCode } from "lucide-react";

export default function Verify() {
  const [params] = useSearchParams();
  const [publicId, setPublicId] = useState(params.get("id") || "");
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (params.get("id")) setPublicId(params.get("id")); }, [params]);

  const verify = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/verify", { public_id: publicId, pin });
      setResult(data);
      toast.success("Documento verificado");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground"><Brain className="h-5 w-5" /></div>
            <p className="font-display text-sm font-bold">Portal de Verificación</p>
          </div>
          <Button variant="ghost" onClick={() => navigate("/")} data-testid="verify-back"><ArrowLeft className="mr-2 h-4 w-4" />Inicio</Button>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-6 py-12 sepp-fade">
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><QrCode className="h-5 w-5" /></div>
          <h1 className="font-display text-2xl font-bold">Verificar autenticidad</h1>
          <p className="mt-1 text-sm text-muted-foreground">Introduce el ID del diálogo y el PIN que aparecen en el documento PDF.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_160px]">
            <div className="space-y-1.5"><Label>ID de diálogo</Label>
              <Input placeholder="SEPP-XXXXXXXX" value={publicId} onChange={(e) => setPublicId(e.target.value)} data-testid="verify-id-input" /></div>
            <div className="space-y-1.5"><Label>PIN</Label>
              <Input placeholder="000000" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="qr-pin-input" /></div>
          </div>
          <Button className="mt-4 w-full" disabled={busy || !publicId || !pin} onClick={verify} data-testid="qr-verify-button">
            {busy ? "Verificando..." : "Verificar documento"}
          </Button>
        </div>

        {result && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-white p-8 sepp-fade" data-testid="verify-result">
            <div className="mb-4 flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="h-5 w-5" /><span className="font-display font-semibold">Documento auténtico</span>
            </div>
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">ID</dt><dd className="font-mono text-primary">{result.public_id}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Matrícula</dt><dd>{result.matricula}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Ejercicio</dt><dd>{result.exercise_title}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Paciente</dt><dd>{result.patient_name}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Estado</dt><dd><StatusBadge status={result.status} /></dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Mensajes</dt><dd>{result.message_count}</dd></div>
            </dl>
            {result.evaluated && <Badge className="mt-4" variant="outline">Calificado por supervisor</Badge>}
            <div className="mt-6 sepp-scroll max-h-80 space-y-2 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4">
              {result.messages.map((m, i) => (
                <div key={i} className={`text-sm ${m.role === "student" ? "text-right" : "text-left"}`}>
                  <span className={`inline-block rounded-lg px-3 py-1.5 ${m.role === "student" ? "bg-primary text-primary-foreground" : "border border-border bg-white"}`}>
                    <b className="text-[10px] uppercase">{m.role === "student" ? "Terapeuta: " : "Paciente: "}</b>{m.content}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
